import { edgeBand, EDGE_BAND_ORDER, formatSigned, marketOutlook } from "./edge.js";
import { mapStoredGame } from "./storedGame.js";
import { poolScoringPicks } from "./history.js";
import { isSundayOrMonday, kickoffWeekday } from "./seasonScorecard.js";

/**
 * Results -> Week's games list: your picks first, then every other game in one
 * list read from the model's side -- the side more likely to win outright at
 * the deadline -- so sorting it by win % is a straight "best picks" order.
 * (It used to split edge sides from no-edge games, which read the no-edge
 * games from the favourite and left their win % below 50.)
 *
 * Every row is graded the same way, off the final score and the frozen line
 * for the side the row is about -- the bet it names -- so the badge, the
 * verdict and the group record can never disagree with each other.
 */

export const GROUPS = [
  { key: "mine", label: "Your picks" },
  { key: "all", label: "All games" },
  { key: "offSlate", label: "Not ranked" },
];

/**
 * How far a side beat its frozen number: its own final margin plus its own
 * frozen line. Positive covered, negative missed, zero a push.
 *
 * Spreads are home-perspective (`league_spread` is the HOME line), so the away
 * side's line is the negation -- a home -3 is +3 to the visitor. Null until
 * the game is final and a frozen line exists.
 */
export function coverMargin(game, side) {
  if (!side || !isFinal(game) || game.league_spread == null) return null;
  if (game.home_score == null || game.away_score == null) return null;

  const home = Number(game.home_score);
  const away = Number(game.away_score);
  const spread = Number(game.league_spread);
  if ([home, away, spread].some(Number.isNaN)) return null;

  const margin = side === "home" ? home - away : away - home;
  const line = side === "home" ? spread : -spread;
  // Lines are half points at worst, so this is exact; the + 0 drops a -0.
  return margin + line + 0;
}

/**
 * Whether a game can be graded. Scores alone are not enough: `SyncWeek` stores
 * them for a game still in progress, and a BUF -3 leading 14-7 at halftime has
 * covered nothing yet. Final means ESPN said `completed`, or the line has
 * already been settled (`ats_result`) -- the settlement job only grades a
 * finished game.
 */
export function isFinal(game) {
  return game.status === "completed" || Boolean(game.ats_result);
}

export function resultFromMargin(margin) {
  if (margin == null) return null;
  if (margin > 0) return "win";
  if (margin < 0) return "loss";
  return "push";
}

/**
 * The side's OWN line value and outright win probability, as of the deadline
 * -- never the other side's negated, which strands the push term (CLAUDE.md).
 * Read off `computeEdge` for the edge side, and off `marketOutlook` for a pick
 * on the other one. Both are percentages; null when there is no market.
 */
function sideOutlook(game, side) {
  const edge = game.edge;
  if (!edge || !edge.measurable) return { lineValue: null, winProbability: null };
  if (edge.side === side) {
    return { lineValue: edge.lineValueProbability, winProbability: edge.winProbability ?? null };
  }

  const stored = mapStoredGame(game);
  if (stored.fivePicksSpread == null) return { lineValue: null, winProbability: null };
  const outlook = marketOutlook(stored.spreads, stored.fivePicksSpread);
  if (!outlook) return { lineValue: null, winProbability: null };
  return side === "home"
    ? { lineValue: outlook.homeLineValue, winProbability: outlook.win }
    : { lineValue: outlook.awayLineValue, winProbability: outlook.loss };
}

// The frozen line's favourite, which is who a no-edge row is read from. A
// pick'em (or no line at all) falls to the home side.
function favouriteSide(game) {
  if (game.league_spread == null) return "home";
  return Number(game.league_spread) <= 0 ? "home" : "away";
}

function trimNumber(value) {
  return String(Number(Math.abs(value).toFixed(1)));
}

/**
 * Where a pick stands while its game is on, for the "Your Picks" row: the
 * score from the picked side and the cover against the frozen line. Takes the
 * stored-game shape (camelCase, `fivePicksSpread` home-perspective). Null
 * unless the game is in progress with a score -- never a result, so nothing
 * counts it until the pick is graded.
 */
export function livePickStatus(game, side) {
  if (game?.status !== "in_progress") return null;
  const home = game.homeScore;
  const away = game.awayScore;
  if (home == null || away == null) return null;
  const us = side === "home" ? home : away;
  const them = side === "home" ? away : home;
  const homeLine = game.fivePicksSpread;
  const line = homeLine == null ? null : (side === "home" ? homeLine : -homeLine);
  const margin = line == null ? null : us - them + line + 0;
  let label = "Live";
  if (margin != null) {
    label = margin === 0 ? "On the number" : `${margin > 0 ? "Covering" : "Short"} by ${trimNumber(margin)}`;
  }
  return { score: `${us}–${them}`, margin, label };
}

const BADGES = { win: "W", loss: "L", push: "P" };

/**
 * "won" when the frozen line covered and the deadline market's number would
 * not have; "cost" when the market's number would have covered and ours did
 * not. The market is a continuous fair line, so it never pushes; a frozen push
 * counts as not covering. Null when the line did not change the outcome.
 */
export function lineDecided(margin, line, marketLine) {
  if (margin == null || line == null || marketLine == null) return null;
  const outright = margin - line;
  const atMarket = outright + marketLine;
  if (margin > 0 && atMarket < 0) return "won";
  if (margin <= 0 && atMarket > 0) return "cost";
  return null;
}

/**
 * One row, read from `side`. `fallbackResult` is a verdict from elsewhere (the
 * commissioner's sheet, or the app's settled pick) used only when the row
 * cannot be graded from the score -- a pick with no frozen line on file.
 */
export function gameRow(game, side, { fallbackResult = null } = {}) {
  const abbr = side === "home" ? game.home_abbr : game.away_abbr;
  const market = game.edge?.market == null || Number.isNaN(game.edge.market) ? null : game.edge.market;
  const sideMarket = market == null ? null : (side === "home" ? market : -market);
  const line = game.league_spread == null
    ? null
    : (side === "home" ? Number(game.league_spread) : -Number(game.league_spread));
  const margin = coverMargin(game, side);
  const graded = resultFromMargin(margin);
  const result = graded || (["win", "loss", "push"].includes(fallbackResult) ? fallbackResult : null);

  const { lineValue, winProbability } = sideOutlook(game, side);
  const band = lineValue == null ? null : EDGE_BAND_ORDER.find((b) => b.key === edgeBand(lineValue));

  const home = game.home_score == null ? null : Number(game.home_score);
  const away = game.away_score == null ? null : Number(game.away_score);
  // Mid-game the bet has not covered anything, but where it stands against its
  // own line is what someone watching wants: "Covering by 3.5". Never a result
  // -- `result` stays null, so no record counts it until the game is final.
  const live = !isFinal(game) && game.status === "in_progress" && home != null && away != null;
  const liveMargin = live && line != null ? (side === "home" ? home - away : away - home) + line + 0 : null;

  let verdict = "Pending";
  if (result === "push") verdict = "Push";
  else if (margin != null) verdict = `${result === "win" ? "Covered" : "Missed"} by ${trimNumber(margin)}`;
  else if (result) verdict = result === "win" ? "Covered" : "Missed";
  else if (line == null && isFinal(game)) verdict = "No line";
  else if (liveMargin != null) {
    verdict = liveMargin === 0 ? "On the number" : `${liveMargin > 0 ? "Covering" : "Short"} by ${trimNumber(liveMargin)}`;
  } else if (live) verdict = "Live";
  const winner = home == null || away == null || home === away ? null : (home > away ? "home" : "away");

  return {
    id: game.id,
    game,
    side,
    bet: line == null ? abbr : `${abbr} ${formatSigned(line)}`,
    lineValue,
    // A strength word only where there is strength to name; Flat says nothing.
    strength: band && band.key !== "flat" ? band.label : null,
    // Every band, Flat included, for the stats line under the row.
    band: band ? band.label : null,
    // The model's outright win chance for this bet at the deadline (percent).
    winProbability,
    // The market at the deadline from this side, signed like `bet`.
    marketLine: sideMarket,
    // Points the frozen number was better (+) or worse (-) than that market.
    lineEdge: line == null || sideMarket == null ? null : line - sideMarket,
    // Whether the stale line decided the bet: "won" when the frozen number
    // covered where the deadline market would not have, "cost" the reverse.
    lineDecided: lineDecided(margin, line, sideMarket),
    margin,
    result,
    badge: BADGES[result] || "—",
    verdict,
    live,
    // Positive covering, negative short, while the game is on; null otherwise.
    liveMargin,
    // The weekday of a Thursday/Friday/Saturday game, which is never ranked
    // (rankBoard) though it is on the commissioner's sheet; null for Sun/Mon.
    offSlate: game.kickoff_at && !isSundayOrMonday(game.kickoff_at) ? kickoffWeekday(game.kickoff_at) : null,
    winner,
  };
}

/**
 * The picker's picks for the week as `{ gameId, side, result }`, or null when
 * they cannot be known.
 *
 * Only a linked account has picks to show: `pool.identified` is what says the
 * standings row -- and so the sheet's five -- belongs to this viewer. The
 * sheet's picks are the record once the week's results PDF lands; before
 * that (a week still being played) the app's own shortlist stands in, trimmed
 * to the five that count by `poolScoringPicks`.
 */
export function yourWeekPicks(games, { identified, weekRow } = {}) {
  if (!identified) return null;

  const sheetPicks = (weekRow && weekRow.picks) || [];
  if (sheetPicks.length) {
    const found = [];
    for (const pick of sheetPicks) {
      const match = findPickedGame(games, pick);
      if (match) found.push({ gameId: match.game.id, side: match.side, result: pick.result || null });
    }
    return found;
  }

  return poolScoringPicks(games.filter((game) => game.pick)).map((game) => ({
    gameId: game.id,
    side: game.pick.side,
    result: game.pick.result || null,
  }));
}

function findPickedGame(games, pick) {
  for (const game of games) {
    if ((pick.abbr && game.home_abbr === pick.abbr) || (pick.team && game.home_team === pick.team)) return { game, side: "home" };
    if ((pick.abbr && game.away_abbr === pick.abbr) || (pick.team && game.away_team === pick.team)) return { game, side: "away" };
  }
  return null;
}

/** W-L, or W-L-P once there is a push. Pending rows count nowhere. */
export function groupRecord(rows) {
  const wins = rows.filter((row) => row.result === "win").length;
  const losses = rows.filter((row) => row.result === "loss").length;
  const pushes = rows.filter((row) => row.result === "push").length;
  const pending = rows.length - wins - losses - pushes;
  return {
    wins,
    losses,
    pushes,
    pending,
    label: pushes ? `${wins}–${losses}–${pushes}` : `${wins}–${losses}`,
  };
}

/**
 * Order within a group. "win" is the model's outright win chance at the
 * deadline, best first -- the number picks are ranked on (CLAUDE.md), so it is
 * "best picks". "edge" is best line value first. Rows with no market sink to
 * the bottom of either (no market is not the same fact as a flat one).
 * "kickoff" is the order the games were played. Kickoff breaks every tie.
 */
export function compareRows(sortKey) {
  const byKickoff = (a, b) => new Date(a.game.kickoff_at) - new Date(b.game.kickoff_at);
  const field = { win: "winProbability", edge: "lineValue" }[sortKey];
  if (!field) return byKickoff;

  return (a, b) => {
    const av = a[field];
    const bv = b[field];
    if (av == null && bv == null) return byKickoff(a, b);
    if (av == null) return 1;
    if (bv == null) return -1;
    return (bv - av) || byKickoff(a, b);
  };
}

/**
 * `sort: "mine"` is Your picks first -- each read from the pick's own side and
 * graded at its own line -- then the rest. Every other game is read from the
 * model's side, and a game you picked in that list stays where the model
 * ranked it, carrying `pick` so the row can highlight it.
 *
 * Everything else is ONE list in the chosen order -- no Edge / No edge split.
 * The split sorted each half on its own, so a Flat game the board took (2026
 * Week 3's DAL +3.5 at 52.1%) sat at the bottom while the Edge game it beat
 * read as fifth best. One list under Win % ranks exactly as Home and the
 * Season Scorecard's board do; the band word on each row still says which
 * games hold line value.
 *
 * Thursday-to-Saturday games, never ranked, go in their own "Not ranked" group
 * below, so a Thursday game's win % cannot sit among the five it is ranking.
 * Kickoff is the exception: it is the order played, where Thursday is first.
 * A pick on one still files under Your picks.
 *
 * `picks` is `yourWeekPicks`'s output: null means they can't be known, and
 * "mine" then falls back to Win %. Empty groups are dropped.
 */
export function groupWeekGames(games, { picks = null, sort = "win" } = {}) {
  const picked = new Map((picks || []).map((pick) => [pick.gameId, pick]));
  const byMine = sort === "mine" && picked.size > 0;
  const splitOffSlate = sort !== "kickoff";
  const groups = { mine: [], all: [], offSlate: [] };

  for (const game of games) {
    const pick = picked.get(game.id);
    if (byMine && pick) {
      groups.mine.push(gameRow(game, pick.side, { fallbackResult: pick.result }));
      continue;
    }
    const side = modelSide(game);
    const row = gameRow(game, side);
    if (pick) {
      const abbr = pick.side === "home" ? game.home_abbr : game.away_abbr;
      row.pick = { side: pick.side, sameSide: pick.side === side, abbr };
    }
    groups[splitOffSlate && row.offSlate ? "offSlate" : "all"].push(row);
  }

  const order = compareRows(sort === "mine" ? "win" : sort);
  return GROUPS
    .map(({ key, label }) => {
      const rows = groups[key].slice().sort(order);
      const record = groupRecord(rows);
      const summary = record.wins + record.losses + record.pushes ? record.label : `${rows.length} pending`;
      const note = key === "offSlate" ? "Thu–Sat, picked before kickoff" : null;
      // Under Win %, the full board's top five are the board's five: a cut
      // line after them shows where every other game sits against that bar.
      // Only on the whole list -- with your picks lifted out the count is off.
      const cutAfter = key === "all" && sort === "win" && rows.length > 5 ? 5 : null;
      return { key, label: key === "all" && byMine ? "Other games" : label, note, rows, record, summary, cutAfter };
    })
    .filter((group) => group.rows.length);
}

function modelSide(game) {
  const edge = game.edge;
  return edge && edge.measurable && edge.side ? edge.side : favouriteSide(game);
}
